import TYPES from '@/types';
import i18n from '@/vue-app/plugins/i18n';
import { currencyFormat } from '@/vue-app/utils/currency';
import { v4 } from 'uuid';

// Application
import CustomerOverviewService from '@/modules/home-dashboard/customer-overview/application/services/customer-overview-service';
import {
  GetCustomerKuspitPositionDetailsQuery,
  GetCustomerKuspitPositionQuery,
} from '@/modules/my-investment/kuspit/customer-kuspit-position/application/queries';
import InvestmentSuggestionCommand
  from '@/modules/investment-suggestion/application/commands/investment-suggestion-command';

// Domain
import {
  InvestmentSuggestionDto,
} from '@/modules/investment-suggestion/domain/dtos/investment-suggestion-dto';
import CustomerOverviewState from '@/modules/home-dashboard/customer-overview/domain/state/customer-overview-state';
import Inject from '@/modules/shared/domain/di/inject';
import { Router } from '@/modules/shared/domain/router';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import { DateFormatter } from '@/modules/shared/domain/date-formatters';

export default class SowosPocketCardViewModel {
  @Inject(TYPES.CUSTOMER_OVERVIEW_SERVICE)
  private readonly customer_overview_service!: CustomerOverviewService;

  @Inject(TYPES.GET_CUSTOMER_KUSPIT_POSITION_QUERY)
  private readonly get_customer_kuspit_position_query!: GetCustomerKuspitPositionQuery;

  @Inject(TYPES.GET_CUSTOMER_KUSPIT_POSITION_DETAILS_QUERY)
  private readonly get_customer_kuspit_position_details_query!:
    GetCustomerKuspitPositionDetailsQuery;

  @Inject(TYPES.CREATE_INVESTMENT_SUGGESTION_COMMAND)
  private readonly create_investment_suggestion_command!: InvestmentSuggestionCommand;

  @Inject(TYPES.ROUTER)
  private readonly router!: Router;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.DATE_FORMATTER)
  readonly date_formatter!: DateFormatter;

  readonly i18n_namespace = 'components.my-investment-dashboard.sowos-pocket-card';

  readonly asset = 'icon_ahorro.svg';

  is_loading = false;

  balance = '0.00 MXN';

  has_kuspit_account = false;

  kuspit_has_funds = false;

  updated_date = '';

  show_kuspit_funding_details_modal = false;

  show_info = false;

  data_update_failure = '';

  bg_image = {
    backgroundImage: '@/assets/images/investment-dashboard/background_blue_sky.svg',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPositionX: 'right',
    backgroundPositionY: 'bottom',
  }

  display_contract_savings = false;

  show_contract_savings_modal = false;

  show_link_account = false;

  has_account = false;

  account_without_contract = false;

  show_recommended = false;

  contract_number = '';

  investment_provider_id = '';

  show_pocket_is_on_maintenance_dialog = false;

  available_pocket_services = 'available';

  kuspit_portal_url = process.env.VUE_APP_KUSPIT_PORTAL_URL;

  product_unavailable_to_hire = '';

  private investment_suggestion_dto: InvestmentSuggestionDto = {
    id: v4(),
    investment_suggestion_analytic_id: '',
    action: 'kuspit',
  }

  get contracting_status() {
    if (this.customer_overview.item.pocket_investment_product_overview.contracting_status === 'has_account') {
      return null;
    }
    return this.customer_overview.item.pocket_investment_product_overview.contracting_status;
  }

  get has_account_without_funds() {
    return this.has_kuspit_account && !this.kuspit_has_funds;
  }

  get position_details_kuspit() {
    return this.get_customer_kuspit_position_details_query.execute();
  }

  customer_overview: CustomerOverviewState = this.customer_overview_service.get_customer_overview();

  loadKuspitData = async () => {
    try {
      this.has_account = this.customer_overview.item.pocket_investment_product_overview.contracted;
      this.has_kuspit_account = this.has_account;
      if (this.has_account) {
        const kuspit_position_data = await this.get_customer_kuspit_position_query.execute();
        this.kuspit_has_funds = kuspit_position_data.has_funds;
        this.data_update_failure = kuspit_position_data.update_data;
        this.updated_date = (kuspit_position_data.update_date
          ? this.date_formatter.formatDate(kuspit_position_data.update_date, 'DD MMM YYYY') : this.translate('not_loaded_date'));
        this.balance = `${currencyFormat(parseFloat(kuspit_position_data.position))} MXN`;

        if (kuspit_position_data.has_funds) {
          const kuspit_position = await this.position_details_kuspit;
          if (kuspit_position) {
            this.contract_number = kuspit_position.contract_id;
          }
        }
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.get_has_kuspit_account'));
    }
  }

  goToPocketDashboard = () => {
    if (this.available_pocket_services === 'available') {
      this.router.navigate('sowos-pocket');
    }
  }

  fundAccount = () => {
    this.show_kuspit_funding_details_modal = true;
  }

  showInfo = ($event: Event) => {
    $event.stopPropagation();
    this.show_info = true;
  }

  start = async () => {
    if (!this.customer_overview.item.pocket_investment_product_overview.available_to_contract) {
      this.product_unavailable_to_hire = 'SOWOS Pocket';
    } else if (this.available_pocket_services === 'available') {
      await this.createInvestmentSuggestionAction();
      if (this.customer_overview.item.pocket_investment_product_overview.contracting_status === 'has_account') {
        this.show_link_account = true;
      } else {
        this.display_contract_savings = true;
        this.show_contract_savings_modal = true;
      }
    } else {
      this.show_pocket_is_on_maintenance_dialog = true;
    }
  }

  translate = (value: string, count?: number, values?: Record<string, string | number>) => (values ? i18n.tc(`${this.i18n_namespace}.${value}`, count, values) as string : i18n.t(`${this.i18n_namespace}.${value}`) as string);

  setBackgroundImage = () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const bg_desktop = require('@/assets/images/investment-dashboard/background_blue_sky.svg');
    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const bg_mobile = require('@/assets/images/investment-dashboard/background_blue_sky.png');
    if (this.kuspit_has_funds || this.contracting_status) {
      const is_mobile = window.innerWidth < 480;
      this.bg_image.backgroundImage = `url(${is_mobile ? bg_mobile : bg_desktop})`;
      this.bg_image.backgroundSize = 'cover';
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const join_pocket = require('@/assets/images/investment-dashboard/join_pocket.svg');
    this.bg_image.backgroundImage = `url(${join_pocket})`;
    this.bg_image.backgroundSize = 'contain';
  }

  setRecommendedProduct = () => {
    const recommended_product = this.customer_overview.item.investor_profile_overview
      .recommended_investment_product?.name;
    this.show_recommended = (recommended_product === 'sowos_pocket');
  }

  createInvestmentSuggestionAction = async () => {
    try {
      const { investment_suggestion_analytic_id, must_be_shown } = this.customer_overview.item
        .investment_suggestion_overview;
      if (must_be_shown) {
        this.investment_suggestion_dto
          .investment_suggestion_analytic_id = investment_suggestion_analytic_id;
        await this.create_investment_suggestion_command.execute(this.investment_suggestion_dto);
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.create_investment_suggestion_action'));
    }
  }

  async initialize() {
    this.is_loading = true;
    await this.loadKuspitData();
    this.setBackgroundImage();
    if (!this.has_kuspit_account && !this.kuspit_has_funds) {
      this.setRecommendedProduct();
    }

    this.is_loading = false;
  }
}
